import { useEffect, useMemo, useState } from "react";
import { maintenanceCampaignProgress } from "@/lib/wartungen";
import { fetchActualHoursByOrderIds } from "@/pages/projekte/hoursUsage";
import type { CampaignView } from "./campaignView";
import { averageTimePerAssetLabel } from "./averageTimePerAsset";

export interface CampaignAverageTime {
  loading: boolean;
  label: string | null;
  detail: string;
}

interface ProjectHoursState {
  hours: number | null;
  error: boolean;
}

/**
 * Für Büromitarbeiter: durchschnittliche Zeit pro gewartetem Objekt.
 * Zähler sind die auf das Projekt geschriebenen Zeiten (Hero, App, Nachunternehmer),
 * Nenner die abgeschlossenen Objekte der jeweiligen Wartung.
 */
export function useCampaignAverageTimes(
  views: CampaignView[],
  enabled: boolean,
): Record<number, CampaignAverageTime> {
  const [hoursByProject, setHoursByProject] = useState<Record<number, ProjectHoursState>>({});

  const projectIdsKey = useMemo(() => {
    if (!enabled) return "";
    const ids = new Set<number>();
    for (const view of views) {
      const id = Number(view.campaign.project_id);
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
    return Array.from(ids)
      .sort((a, b) => a - b)
      .join(",");
  }, [enabled, views]);

  useEffect(() => {
    if (!projectIdsKey) {
      setHoursByProject({});
      return;
    }
    let cancelled = false;
    const ids = projectIdsKey.split(",").map((part) => Number(part));
    void fetchActualHoursByOrderIds(ids).then((result) => {
      if (cancelled) return;
      const next: Record<number, ProjectHoursState> = {};
      for (const id of ids) {
        const row = result.get(id);
        next[id] = {
          hours: row && !row.error ? row.actualHours : null,
          error: !row || row.error || row.actualHours == null,
        };
      }
      setHoursByProject(next);
    });
    return () => {
      cancelled = true;
    };
  }, [projectIdsKey]);

  return useMemo(() => {
    const out: Record<number, CampaignAverageTime> = {};
    if (!enabled) return out;
    const pending = projectIdsKey.length > 0 && Object.keys(hoursByProject).length === 0;
    for (const view of views) {
      const campaignId = view.campaignId;
      if (!campaignId) continue;
      const completed = maintenanceCampaignProgress(view.campaign)?.completed ?? 0;
      const projectId = Number(view.campaign.project_id);
      if (!(completed > 0) || !(projectId > 0)) continue;
      const hours = hoursByProject[projectId];
      if (!hours) {
        if (!pending && !projectIdsKey.split(",").includes(String(projectId))) continue;
        out[campaignId] = {
          loading: true,
          label: null,
          detail: "Projektzeiten werden geladen",
        };
        continue;
      }
      if (hours.error || hours.hours == null) {
        out[campaignId] = {
          loading: false,
          label: "Zeit pro Objekt nicht verfügbar",
          detail: "Die Zeiten des Projekts konnten nicht geladen werden.",
        };
        continue;
      }
      const described = averageTimePerAssetLabel(hours.hours, completed);
      if (!described) continue;
      out[campaignId] = { loading: false, label: described.label, detail: described.detail };
    }
    return out;
  }, [enabled, views, hoursByProject, projectIdsKey]);
}
